import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import { useCreateMonitoringLocation } from "@/hooks/use-monitoring-locations";
import { useFacilities, useFacility } from "@/hooks/use-facilities";
import { useToast } from "@/hooks/use-toast";
import { LocationForm } from "@/components/monitoring-locations/location-form";
import { PageHeader, Breadcrumbs, Select } from "@/components/ui";

interface SearchParams {
  facilityId?: string;
}

export const Route = createFileRoute("/_authenticated/monitoring-locations/new")({
  component: UnifiedCreateLocationPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    facilityId: typeof search.facilityId === "string" ? search.facilityId : undefined,
  }),
});

function UnifiedCreateLocationPage() {
  const { t } = useTranslation("monitoringLocations");
  const navigate = useNavigate();
  const toasts = useToast();
  const { facilityId: urlFacilityId } = Route.useSearch();

  const orgId = useAppStore((s) => s.activeOrgId);

  const [selectedFacilityId, setSelectedFacilityId] = useState(urlFacilityId ?? "");
  const activeFacilityId = selectedFacilityId || urlFacilityId || "";

  const { data: facilitiesData } = useFacilities(orgId, {
    limit: 100,
    status: "active",
  });
  const facilities = facilitiesData?.data ?? [];

  const { data: facility } = useFacility(orgId, activeFacilityId);
  const facilityName = facility?.name ?? activeFacilityId;

  const mutation = useCreateMonitoringLocation(orgId, activeFacilityId);

  const breadcrumbs = activeFacilityId
    ? [
        { label: t("title", { ns: "facilities" }), to: "/facilities" },
        {
          label: facilityName,
          to: `/facilities/${activeFacilityId}`,
        },
        {
          label: t("title"),
          to: `/monitoring-locations?facilityId=${activeFacilityId}`,
        },
        { label: t("addLocation") },
      ]
    : [
        {
          label: t("title"),
          to: "/monitoring-locations",
        },
        { label: t("addLocation") },
      ];

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader title={t("addLocation")} />

      {!activeFacilityId && (
        <div className="mt-6">
          <label className="mb-2 block text-sm font-medium text-on-surface">
            {t("chooseFacility")}
          </label>
          <Select
            className="w-80"
            value={selectedFacilityId}
            onChange={(e) => setSelectedFacilityId(e.target.value)}
            placeholder={t("chooseFacility")}
            options={facilities.map((f) => ({
              value: f.id,
              label: f.name,
            }))}
          />
        </div>
      )}

      {activeFacilityId && (
        <div className="mt-6">
          <LocationForm
            mode="create"
            isSubmitting={mutation.isPending}
            onSubmit={(values) => {
              mutation.mutate(values, {
                onSuccess: (created) => {
                  toasts.success("monitoringLocations:created");
                  if (created) {
                    void navigate({
                      to: "/monitoring-locations/$locationId",
                      params: { locationId: created.id },
                      search: { facilityId: activeFacilityId },
                    });
                  }
                },
                onError: () => toasts.error("monitoringLocations:createFailed"),
              });
            }}
          />
        </div>
      )}
    </div>
  );
}
